import { environment } from '../../../environments/environment';
import { getDomain } from 'tldts';

export interface OAuthParams {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  state: string;
}

export function getOAuthParams(params: Record<string, unknown>): OAuthParams | undefined {
  const clientId = getStringParam(params['client_id']);
  const redirectUri = getStringParam(params['redirect_uri']);
  const responseType = getStringParam(params['response_type']);
  const scope = getStringParam(params['scope']);
  const state = getStringParam(params['state']);

  if (!clientId || !redirectUri || !responseType || !scope || !state) {
    return undefined;
  }

  return {
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: responseType,
    scope,
    state,
  };
}

export function isOAuthParamsValid(params: OAuthParams): boolean {
  if (!params || params.client_id !== environment.cognito.userPoolClientId) {
    return false;
  }

  try {
    const redirectHostname = new URL(params.redirect_uri).hostname.toLowerCase();
    const isAllowedRedirectDomain = (environment.oauthAllowedRedirectDomains || []).some(domain => {
      const normalizedDomain = domain.trim().toLowerCase();
      if (!normalizedDomain) {
        return false;
      }

      if (normalizedDomain === 'localhost') {
        return redirectHostname === 'localhost';
      }

      return redirectHostname === normalizedDomain || redirectHostname.endsWith(`.${normalizedDomain}`);
    });

    return isAllowedRedirectDomain;
  } catch {
    return false;
  }
}

export function redirectToHostedUiAuthorize(params: OAuthParams, username?: string): void {
  const domain = environment.cognitoHostedUi.domain.replace(/\/$/, '');
  const queryParams = new URLSearchParams({
    client_id: params.client_id,
    redirect_uri: params.redirect_uri,
    response_type: params.response_type,
    scope: params.scope,
    state: params.state,
  });

  if (username) {
    const emailDomain = username.split('@')[1];
    if (emailDomain) {
      queryParams.set('idp_identifier', emailDomain);
    }
    queryParams.set('login_hint', username);
  }

  window.location.assign(`${domain}/oauth2/authorize?${queryParams.toString()}`);
}

function getStringParam(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}